import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { apiCreateListingApplication } from '#/api/listingApplication/apiCreateListingApplication';
import { apiDeleteTmpStorage } from '#/api/tmpStorage/apiDeleteTmpStorage';
import type {
  ApiListingApplicationId,
  ApiTmpStorageId,
} from '#/api/types/ApiTypedId';
import type { FormErrorResponse } from '#/components/Form/FormContext';
import { getFormErrorArray } from '#/components/Form/getFormErrorArray';
import { LoadingSection } from '#/components/LoadingSection/LoadingSection';
import {
  useClientEnvironment,
  useUser,
} from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useQueryParamState } from '#/hooks/useQueryParamState';
import { useI18n } from '#/i18n/hooks/useI18n';
import { listingApiClientFetchTmpStorage } from '#/modules/listing/api/listingApiFetchTmpStorage';
import { useListingApplicationSubmissionAsUser } from '#/modules/listing/hooks/listingApplicationSubmission/useListingApplicationSubmissionAsUser';
import type { ApiInternship } from '#/modules/listing/internships/api/types/ApiInternship';
import type { ApiJob } from '#/modules/listing/jobs/api/types/ApiJob';
import { useShowMarkAsAppliedToast } from '#/modules/listing/jobs/pages/view/useShowMarkAsAppliedToast';
import type { ListingTmpStorageListingApplicationFailure } from '#/modules/listing/types/ListingTmpStorageListingApplicationFailure';
import type { ApiVolop } from '#/modules/listing/volops/api/types/ApiVolop';

type Props = {
  listing: ApiJob | ApiInternship | ApiVolop;
  children: (data: {
    initialListingApplicationId: ApiListingApplicationId | null;
    listingApplicationFailure: ListingTmpStorageListingApplicationFailure | null;
  }) => ReactNode;
};

export function CreateListingApplicationFromTmpStorageContainer({
  listing,
  children,
}: Props) {
  const { i18n } = useI18n();
  const { user } = useUser();
  const { clientEnvironment } = useClientEnvironment();

  const [
    listingApplicationTmpIdQueryParam,
    setListingApplicationTmpIdQueryParam,
  ] = useQueryParamState({ name: 'listingApplicationTmpId' });

  const [result, setResult] = useKeyedState<
    | { type: 'success'; listingApplicationId: ApiListingApplicationId }
    | { type: 'error'; failure: ListingTmpStorageListingApplicationFailure }
    | null
  >(null, {
    key: `${listing.id}_${user?.id}`,
  });

  const readyToProcess = Boolean(
    user &&
    user.emailVerified &&
    user.firstName &&
    user.lastName &&
    clientEnvironment,
  );

  const listingApplicationTmpIdToProcess =
    readyToProcess && !result && listingApplicationTmpIdQueryParam
      ? listingApplicationTmpIdQueryParam
      : null;

  const { submitListingApplicationAsUser } =
    useListingApplicationSubmissionAsUser({
      listingId: listing.id,
      listingType: listing.type,
      orgId: listing.org?.id ?? null,
      orgType: listing.org?.orgType ?? null,
      onApplied: (listingApplicationId) =>
        setResult({ type: 'success', listingApplicationId }),
      listingApplicationFailure:
        result?.type === 'error' ? result.failure : null,
    });

  const { showMarkAppliedToast } = useShowMarkAsAppliedToast();

  const fetchTmpStorageAndCreateListingApplication = useCallback(
    async (tmpId: ApiTmpStorageId) => {
      const tmpStorage = await listingApiClientFetchTmpStorage(tmpId);
      if (!tmpStorage || !user || !clientEnvironment) return;

      await apiDeleteTmpStorage(tmpId);

      switch (tmpStorage.type) {
        case 'mark-as-applied': {
          try {
            const listingApplication = await apiCreateListingApplication({
              id: tmpStorage.jobId,
              type: 'JOB',
              requestedAttachmentsHash: '',
            });

            setResult({
              type: 'success',
              listingApplicationId: listingApplication.id,
            });
            showMarkAppliedToast();
          } catch {
            // if for some reason it was not possible to mark as applied, just move forward
          }

          setListingApplicationTmpIdQueryParam(undefined);
          return;
        }

        case 'listing-application':
          try {
            await submitListingApplicationAsUser({
              user,
              values: tmpStorage.initialValues,
            });
            // if submitListingApplicationAsUser succeeds it will navigate
            // no need to setListingApplicationTmpId
          } catch (e) {
            setListingApplicationTmpIdQueryParam(undefined);
            setResult({
              type: 'error',
              failure: {
                initialValues: tmpStorage.initialValues,
                errors: getFormErrorArray(
                  i18n,
                  e as FormErrorResponse<unknown>,
                ).reduce(
                  (acc, errorData) =>
                    Object.assign(acc, {
                      [errorData.name as string]: errorData.description,
                    }),
                  {} as Record<string, string>,
                ),
              },
            });
          }
      }
    },
    [
      i18n,
      user,
      clientEnvironment,
      submitListingApplicationAsUser,
      setResult,
      showMarkAppliedToast,
      setListingApplicationTmpIdQueryParam,
    ],
  );

  const processedTmpIdRef = useRef<ApiTmpStorageId | null>(null);

  useEffect(() => {
    if (
      listingApplicationTmpIdToProcess &&
      processedTmpIdRef.current !== listingApplicationTmpIdToProcess
    ) {
      processedTmpIdRef.current = listingApplicationTmpIdToProcess;
      void fetchTmpStorageAndCreateListingApplication(
        listingApplicationTmpIdToProcess,
      );
    }
  }, [
    listingApplicationTmpIdToProcess,
    result,
    fetchTmpStorageAndCreateListingApplication,
    setListingApplicationTmpIdQueryParam,
    setResult,
  ]);

  if (listingApplicationTmpIdToProcess) return <LoadingSection />;

  return (
    <>
      {children({
        initialListingApplicationId:
          result?.type === 'success' ? result.listingApplicationId : null,
        listingApplicationFailure:
          result?.type === 'error' ? result.failure : null,
      })}
    </>
  );
}
